import { TFunction } from 'i18next'

import { appConfig } from '@/app-config/appConfig'
import { formatUserDate, getSmartDate } from '@/lib/datetime'
import { isNil } from '@/lib/isNil'
import { formatCurrency } from '@/lib/number-utils'
import { RouteParams, RouteWithParams } from '@/lib/route-utils'
import { Routes } from '@/lib/routes'
import { darkBlue, darkRed, linkBlue, purple, successGreen, yellow } from '@/refactor/colors'
import { TimePeriodDetailed } from '@/travelsuit'
import { fullName } from '@/travelsuit/users'
import { UserCurrency } from '@/types/common'
import { DailyAllowanceItem, DailyAllowanceModel } from '@/types/expenses/dailyAllowance'
import {
	ExpenseModel,
	ExpenseResponseAttendee,
	ExpenseStatus,
	ExpenseType,
	TaxAndVatRate,
} from '@/types/expenses/expense'
import { MileageModel, MileageRate } from '@/types/expenses/mileage'
import { ExpenseFileMandatory } from '@/types/expenses/policy'
import { ExpenseReportModel, ExpenseReportStatus } from '@/types/expenses/report'
import { StandardExpenseModel } from '@/types/expenses/standardExpense'
import { CountriesCode } from '@/types/locale'
import { User as UserBase } from '@/types/user'

import { TimeOptions } from './index'

export type ExpenseReportSubmitter = User

export type ExpenseReportsFilterSet = {
	timePeriod: TimeOptions | TimePeriodDetailed
	invoiceProfiles: Map<number, boolean>
	submitters: Map<number, boolean>
	statuses: Map<ExpenseReportStatus, boolean>
	assignedToMe?: boolean
	searchQuery?: string
}

export type ExpenseReportsExportApi = Partial<
	TimePeriodDetailed & {
		invoice_profiles: number[]
		submitters: number[]
		statuses: ExpenseReportStatus[]
		search: string
		assigned_to_me: boolean
	}
>

export const getRate = (mileageRate: MileageRate) => {
	return `${formatCurrency(mileageRate.amount, { currency: mileageRate.currency })}/${mileageRate.unit_of_length}`
}

export function getFileMandatory(): Promise<ExpenseFileMandatory> {
	return Promise.resolve({ standard: true, mileage: true, daily_allowance: false })
}

export const getExpenseReportStatusLabel: (t: TFunction) => Record<ExpenseReportStatus, string> = (t) => ({
	draft: t('expenses.status-draft'),
	submitted: t('expenses.status-submitted'),
	approved: t('expenses.status-approved'),
	rejected: t('expenses.status-rejected'),
	reimbursed: t('expenses.status-reimbursed'),
})

export const getExpenseStatusLabel: (t: TFunction) => Record<ExpenseStatus, string> = (t) => ({
	open: t('expenses.status-open'),
	submitted: t('expenses.status-submitted'),
	approved: t('expenses.status-approved'),
	rejected: t('expenses.status-rejected'),
	reimbursed: t('expenses.status-reimbursed'),
})

export const getExpenseTypeLabels: (t: TFunction) => Record<ExpenseType, string> = (t) => ({
	standard: t('expenses.standard-expense'),
	mileage: t('expenses.mileage'),
	daily_allowance: t('expenses.daily-allowance'),
})

export const expenseTypeOrder: Record<ExpenseType, number> = { standard: 0, mileage: 1, daily_allowance: 2 }

// todo: fix circular: User => TravelProfile => Group => User
type User = Omit<UserBase, 'traveler_profile'>

export type ReimbursableFilterOptions = 'reimbursable' | 'non-reimbursable'

export type ExpensesFilterSet = {
	timePeriod: TimeOptions | TimePeriodDetailed
	types: Map<ExpenseType, boolean>
	statuses: Map<ExpenseStatus, boolean>
	submitters: Map<number, boolean>
	attendees: Map<number, boolean>
	reimbursable: Map<ReimbursableFilterOptions, boolean>
	unassigned?: boolean
	searchQuery?: string
}

export type ExpensesExportApi = Partial<
	TimePeriodDetailed & {
		expense_types: string[]
		expense_statuses: string[]
		submitters: number[]
		attendees: number[]
		reimbursable_types: [boolean] | [true, false]
		unassigned: boolean
		search: string
	}
>

export type ExpensePolicy = {
	expense_approver?: User
	fallback_approver?: User
	categories_by_country?: ExpenseCategories
}

export type CategoryRate = 'general' | 'categories' | 'mileage' | 'tax-and-vat' | 'daily-allowances'

export type ExpenseCategory = string
export type ExpenseCategories = Partial<Record<CountriesCode, Array<ExpenseCategory>>>

export const rawVatRateNone = { rate: 0, id: -1, name: 'select.none' } as const
export function getVatRateNone(t: TFunction): TaxAndVatRate {
	return { ...rawVatRateNone, name: t(rawVatRateNone.name) } as TaxAndVatRate
}

export const normalizeUrl = (url?: string) => {
	if (!url) {
		return ''
	}
	if (url.startsWith('/') || url.toLowerCase().startsWith('http')) {
		return url
	}

	// todo: it could be just window.location but won't work locally - refactor it
	let origin
	try {
		origin = new URL(appConfig.API_DOMAIN).origin
	} catch (e) {
		;({ origin } = window.location)
	}
	return `${origin.replace('api-', '')}/${url}`
}

export function removeDisabledValuesInFilter<T extends number | string>(filter: Map<T, boolean>) {
	return Array.from(filter)
		.filter(([, isEnabled]) => isEnabled)
		.map(([v]) => v)
}

export function getDailyAllowanceAmount(rate: DailyAllowanceItem) {
	return rate.daily_allowance_rate && rate.number_of_days
		? rate.daily_allowance_rate.amount * Number(rate.number_of_days)
		: 0
}

export const DAILY_ALLOWANCE_RATES_LIMIT = 50

export const getAttendeeFullName = (attendee: ExpenseResponseAttendee) => {
	return attendee.is_internal ? fullName(attendee) : `${fullName(attendee)} (${attendee.company_name})`
}

export abstract class ExpenseHandler<T extends ExpenseModel = ExpenseModel> {
	abstract getMerchant(t: TFunction): string
	abstract getAttendees(): ExpenseResponseAttendee[]
	abstract getFormattedAmount(): string
	abstract canSave(): boolean
	abstract canSubmit(): Promise<boolean> | boolean
	abstract getDate(): Date | [Date, Date] | null | undefined
	abstract getFormattedDate(user: User): string
	abstract getRouteToEditExpense(): readonly [RouteWithParams<Routes>, RouteParams<Routes>]
	abstract getRouteToEditExpenseInReport(): readonly [RouteWithParams<Routes>, RouteParams<Routes>]

	constructor(public readonly data: T) {}

	isSubmitter(user: User) {
		return this.data.submitter.id === user.id
	}

	isReadOnly(user: User) {
		const excludedStatuses: (typeof this.data.status)[] = ['open', 'rejected']
		return !excludedStatuses.includes(this.data.status) || !this.isSubmitter(user)
	}

	getTax(amount: number | null, t: TFunction): string | number {
		return t('Not applicable')
	}

	getStatusLabel(t: TFunction) {
		return getExpenseStatusLabel(t)[this.data.status]
	}

	getStatusColor() {
		return {
			open: linkBlue,
			submitted: yellow,
			approved: successGreen,
			reimbursed: purple,
			rejected: darkRed,
		}[this.data.status]
	}
}

export class StandardExpenseHandler extends ExpenseHandler<StandardExpenseModel> {
	static expenseRequiredFields: Record<keyof StandardExpenseModel, boolean> = {
		id: true,
		amount: true,
		attendees: false,
		category: false,
		createdAt: true,
		currency: true,
		date: true,
		description: false,
		file: false,
		isTravelRelated: false,
		merchant: true,
		merchantCountry: true,
		ocr: false,
		reimbursable: true,
		report: false,
		status: true,
		submitter: true,
		type: true,
		vatRate: true,
	}

	getMerchant(t: TFunction) {
		return this.data.merchant || t('Unknown')
	}

	getFormattedAmount() {
		return this.data.amount ? formatCurrency(this.data.amount, { currency: this.data.currency, precision: 2 }) : ''
	}

	getAttendees() {
		return this.data.attendees
	}

	canSave() {
		return Object.keys(StandardExpenseHandler.expenseRequiredFields)
			.filter((key: keyof StandardExpenseModel) => StandardExpenseHandler.expenseRequiredFields[key])
			.every((key: keyof StandardExpenseModel) => typeof this.data[key] === 'boolean' || Boolean(this.data[key]))
	}

	async canSubmit() {
		return this.canSave() && (await getFileMandatory()).standard ? Boolean(this.data.file) : true
	}

	getDate() {
		return this.data.date
	}

	getFormattedDate(user: User): string {
		return this.data.date ? formatUserDate(this.data.date, user) : ''
	}

	getTax(amount: number | null, t: TFunction) {
		const { vatRate } = this.data
		if (isNil(amount) || isNil(vatRate) || vatRate.id === rawVatRateNone.id) {
			return t('Not applicable')
		}

		return parseFloat((amount - amount / (1 + vatRate.rate / 100)).toFixed(2))
	}

	getRouteToEditExpense() {
		return [Routes.EditStandardExpense, { id: this.data.id }] as const
	}

	getRouteToEditExpenseInReport() {
		return [Routes.EditStandardExpenseInReport, { id: this.data.id }] as const
	}

	resetVatRateIfDeleted(user: UserBase, vatRates: TaxAndVatRate[]) {
		if (this.data.vatRate?.id === rawVatRateNone.id) {
			return
		}

		const isRateValid = vatRates
			.filter((vatRate) => vatRate.country.code === user?.invoice_profile?.country?.code)
			.filter((vatRate) => vatRate.all_countries || vatRate.merchant_country.code === this.data.merchantCountry?.code)
			.some((vatRate) => vatRate.id === this.data.vatRate?.id)

		if (!isRateValid) {
			this.data.vatRate = undefined
		}
	}
}

export class MileageHandler extends ExpenseHandler<MileageModel> {
	getMerchant() {
		return `${this.data.distance.inputValue}${this.data.rate.unit_of_length} (${getRate(this.data.rate)})`
	}

	getFormattedAmount() {
		return formatCurrency(this.data.amount, { currency: this.data.rate.currency, precision: 2 })
	}

	getAttendees() {
		return this.data.attendees
	}

	getDate() {
		return this.data.date
	}

	getFormattedDate(user: User): string {
		return formatUserDate(this.data.date, user)
	}

	canSave() {
		return true
	}

	canSubmit() {
		return this.canSave()
	}

	getRouteToEditExpense() {
		return [Routes.EditMileage, { id: this.data.id }] as const
	}

	getRouteToEditExpenseInReport() {
		return [Routes.EditMileageInReport, { id: this.data.id }] as const
	}
}

export class DailyAllowanceHandler extends ExpenseHandler<DailyAllowanceModel> {
	getMerchant() {
		return this.data.destination.name
	}

	getFormattedAmount() {
		return formatCurrency(this.data.amount, { currency: UserCurrency.EUR, precision: 2 })
	}

	canSave() {
		return true
	}

	async canSubmit() {
		return this.canSave() && (await getFileMandatory()).daily_allowance ? Boolean(this.data.file) : true
	}

	getAttendees() {
		return []
	}

	getDate() {
		return [this.data.startDate, this.data.endDate] as [Date, Date]
	}

	getFormattedDate(): string {
		return getSmartDate(this.data.startDate, this.data.endDate)
	}

	getRouteToEditExpense() {
		return [Routes.EditDailyAllowance, { id: this.data.id }] as const
	}

	getRouteToEditExpenseInReport() {
		return [Routes.EditDailyAllowanceInReport, { id: this.data.id }] as const
	}
}

export class ExpenseReportHandler {
	constructor(public readonly data: ExpenseReportModel) {}

	isSubmitter(user: User) {
		return this.data.submitter.id === user.id
	}

	getAttendees(): ExpenseResponseAttendee[] {
		return Array.from(
			this.data.expenses
				.reduce(
					(attendees, expense) =>
						expense
							.getAttendees()
							.reduce(
								(attendees: Map<number, ExpenseResponseAttendee>, attendee: ExpenseResponseAttendee) =>
									attendees.set(attendee.id, attendee),
								attendees,
							),
					new Map<number, ExpenseResponseAttendee>(),
				)
				.values(),
		)
	}

	getTotalAmount<T extends ExpenseType, U extends number>(): Map<T, U> {
		return (
			this.data.expenses?.reduce(
				(sums, expense) =>
					sums.set(expense.data.type, (expense.data.report?.amount || 0) + (sums.get(expense.data.type) || 0)),
				new Map<T, U>(),
			) ?? new Map<T, U>()
		)
	}

	isReadonly(user: User) {
		const excludedStatuses: (typeof this.data.status)[] = ['draft', 'rejected']
		return !excludedStatuses.includes(this.data.status) || user.id !== this.data.submitter.id
	}

	getStatusLabel(t: TFunction) {
		return getExpenseReportStatusLabel(t)[this.data.status]
	}

	getStatusColor() {
		return {
			draft: darkBlue,
			submitted: yellow,
			approved: successGreen,
			reimbursed: purple,
			rejected: darkRed,
		}[this.data.status]
	}

	static getStartEndDate(expenses: Array<ExpenseHandler>): [Date, Date] | null {
		if (!expenses.length) {
			return null
		}
		const MAX_DATE = new Date('2999-12-31').getTime()

		const minDate = expenses.reduce((minDate, expense) => {
			const dates = expense.getDate()
			if (!dates) {
				return minDate
			}

			const date = Array.isArray(dates) ? dates[0] : dates
			return date.getTime() < minDate ? date : minDate
		}, MAX_DATE)

		if (minDate === MAX_DATE) {
			return null
		}

		const maxDate = expenses.reduce((maxDate, expense) => {
			const dates = expense.getDate()
			if (!dates) {
				return maxDate
			}

			const date = Array.isArray(dates) ? dates[1] : dates
			return date.getTime() > maxDate ? date : maxDate
		}, 0)

		return [new Date(minDate), new Date(maxDate)]
	}

	static getTotalReimbursable(expenseReport: Partial<ExpenseReportModel>): number {
		return (
			expenseReport.expenses?.reduce(
				(sum, expense) =>
					sum + (expense.data.reimbursable && expense.data.report?.amount ? expense.data.report.amount : 0),
				0,
			) ?? 0
		)
	}

	static getTotalAmount<T extends ExpenseType, U extends number>(
		expenseReport: Partial<ExpenseReportModel>,
	): Map<T, U> {
		return (
			expenseReport.expenses?.reduce(
				(sums, expense) =>
					sums.set(expense.data.type, (expense.data.report?.amount || 0) + (sums.get(expense.data.type) || 0)),
				new Map<T, U>(),
			) ?? new Map<T, U>()
		)
	}

	static getTotalTax(expenseReport: Partial<ExpenseReportModel>, t: TFunction) {
		let allNil = true

		const total =
			expenseReport.expenses?.reduce((sums, expense) => {
				const tax = expense.getTax(expense.data.report?.amount ?? null, t)
				if (typeof tax === 'string') {
					return sums
				}
				allNil = false

				return sums + tax
			}, 0) ?? 0

		return allNil ? t('Not applicable') : formatCurrency(total)
	}
}

export const FILE_UPLOAD_ACCEPT = 'image/png, image/jpeg, application/pdf'
